import { DOCUMENT, EnvironmentProviders, ErrorHandler, inject, InjectionToken, provideAppInitializer, Provider } from '@angular/core'
import { MatIconRegistry } from '@angular/material/icon'
import { MAT_SNACK_BAR_DEFAULT_OPTIONS, MatSnackBarConfig } from '@angular/material/snack-bar'
import { DomSanitizer } from '@angular/platform-browser'
import { provideLottieOptions } from 'ngx-lottie'
import {
  ConnectionService,
  DownloadService,
  EasterEggService,
  GlobalErrorHandlerService,
  SnackbarService,
  ToolbarService,
} from './services'
import { ErrorCenterService } from './services/error-center.service'
import { StageService } from './services/stage.service'
import { zahlenfeldOhneMausrad } from './util/zahlenfeld-mausrad.util'

export const LIB_LOGIN_CONFIG = new InjectionToken<LoginConfig>('LIB_LOGIN_CONFIG')
export const LIB_STAGE = new InjectionToken<Stage>('LIB_STAGE')

/**
 * Wie geteilte Texte die Leserin ansprechen: Nutzer mit Login (Leistungserbringer im Portal,
 * SaniUp-Mitarbeitende in der internen App) werden geduzt, Patient:innen im Selfservice gesiezt.
 * Gesetzt ueber `provideSharedService({ anrede })`; ohne Angabe gilt `du`.
 * Regel: `.claude/rules/wording.md`, Abschnitt „Anrede: Nutzer duzen, Patient:innen siezen".
 */
export type Anrede = 'du' | 'sie'
export const LIB_ANREDE = new InjectionToken<Anrede>('LIB_ANREDE', { factory: () => 'du' })

export interface LoginConfig {
  /**
   * Redirection nach dem Login (von der Login Page)
   */
  internalRedirectUri: string

  /**
   * Pfad zur Benutzerverwaltung DIESER App, etwa `/admin?tab=benutzer`.
   *
   * Steht er, erklaert der Dialog „So bekommst du einen Zugang" den Weg dorthin und legt den
   * fertigen Link in die Nachricht zum Weiterschicken. Fehlt er, hat die App keine
   * Benutzerverwaltung, die ein Kunde bedienen koennte (interne Webapp) — der Dialog bleibt dann
   * bei der Nachricht und dem Support-Hinweis.
   */
  benutzerverwaltungPfad?: string
}

export type Stage = 'env-production' | 'env-demo' | 'env-test' | 'env-local' | 'development'

/**
 * Stages, auf denen echte Menschen zusehen: Produktion — und Demo, weil dort Kundentermine laufen.
 * Entwickler-Hilfen gehören auf beiden aus: NGXS' `developmentMode` friert bei JEDER Action den
 * kompletten State ein (bei den grossen Dashboard-Listen sichtbar), und die Redux-Devtools
 * spiegeln zusätzlich jeden State-Übergang mit.
 *
 * Bewusst NICHT `!stageService.isProd()` verwenden — das ist die Frage „ist es Produktion",
 * hier geht es um „schaut jemand zu".
 */
export function isCustomerFacingStage(stage: Stage): boolean {
  return stage === 'env-production' || stage === 'env-demo'
}

export interface SharedConfig {
  loginConfig?: LoginConfig
  stage: Stage
  /** Anrede in geteilten Texten, siehe `LIB_ANREDE`. */
  anrede?: Anrede
}

export function provideSharedService(sharedConfig: SharedConfig): Provider[] {
  const providers: Provider[] = [
    {
      provide: LIB_STAGE,
      useValue: sharedConfig.stage,
    },
    {
      provide: LIB_ANREDE,
      useValue: sharedConfig.anrede ?? 'du',
    },
    ErrorCenterService,
    // Beobachtet ueber den errorInterceptorFn, ob SaniUp antwortet, und laesst haengengebliebene
    // Ansichten von allein nachladen. Muss vor dem ersten HTTP-Aufruf stehen — der Interceptor
    // injiziert ihn.
    ConnectionService,
    StageService,
    { provide: ErrorHandler, useClass: GlobalErrorHandlerService },
    // Meldungen stehen in allen drei Apps unten rechts — nah an den Speichern-/Absenden-Knoepfen,
    // und weder ueber der Kopfleiste noch mitten in einer Tabelle. Die Standzeit setzt der
    // SnackbarService je Meldung selbst; der Wert hier greift nur, falls jemand MatSnackBar
    // direkt benutzt (soll er nicht — siehe libs/shared/CLAUDE.md).
    {
      provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
      useValue: <MatSnackBarConfig>{ duration: 4000, horizontalPosition: 'right', verticalPosition: 'bottom' },
    },
    SnackbarService,
    ToolbarService,
    EasterEggService,
    DownloadService,
    provideLottieOptions({
      // Der "light"-Build laesst den Expression-Interpreter weg. Der ist der einzige Nutzer von
      // `eval()` in lottie-web und loeste bei jedem Build eine Bundler-Warnung aus; ausserdem
      // spart der Verzicht rund ein Drittel des lottie-Chunks. Preis: After-Effects-Expressions
      // in einer .json-Animation werden ignoriert, die Eigenschaft bleibt auf ihrem Keyframe-Wert.
      player: () => import('lottie-web/build/player/lottie_light'),
    }),
  ]

  if (sharedConfig.loginConfig) {
    providers.push({
      provide: LIB_LOGIN_CONFIG,
      useValue: sharedConfig.loginConfig,
    })
  }

  return providers
}

export function provideWhatsappIcon(): EnvironmentProviders {
  return provideAppInitializer(() => {
    const matIconRegistry = inject(MatIconRegistry)
    const sanitzier = inject(DomSanitizer)
    matIconRegistry.addSvgIcon('whatsapp', sanitzier.bypassSecurityTrustResourceUrl('/assets/icons/whatsapp_black.svg'))
  })
}

/**
 * `autocomplete`-Wert, den kein Browser kennt. Chrome und Edge ignorieren `autocomplete="off"` bei
 * Adressfeldern; ein unbekannter Wert nimmt das Feld dagegen aus ihrer Adresserkennung heraus.
 */
const KEIN_AUTOFILL = 'saniup-kein-autofill'
const FELDER = 'input, textarea'

/**
 * Schaltet das Ausfuellen und Speichern von Adressen durch den Browser fuer ALLE Felder der App ab,
 * auch in Dialogen (die liegen im Overlay ausserhalb des App-Baums).
 *
 * In den Mitarbeiter-Apps sind die Adressen in den Formularen die von Patient:innen: Edge bot nach
 * dem Speichern „Adresse speichern?" an und haette sie ins Browserprofil der Mitarbeiterin
 * uebernommen, samt Sync ins Microsoft-Konto, und beim naechsten Patienten wieder vorgeschlagen.
 * Nicht in der Selfservice-App: dort traegt die Patient:in ihre eigene Adresse ein.
 *
 * Ein Feld, das bewusst ausgefuellt werden darf (der eigene Name im Profil), traegt
 * `data-browser-autofill`. Gegen das Speichern ist das kein Schloss, sondern das Beste, was eine
 * Seite tun kann. Sicher abschalten laesst es sich nur im Browser (Edge-Richtlinie
 * `AutofillAddressEnabled`).
 */
export function provideKeinBrowserAutofill(): EnvironmentProviders {
  return provideAppInitializer(() => {
    const document = inject(DOCUMENT)
    const markieren = (feld: Element) => {
      // "off" bleibt stehen: es haelt in Suchfeldern und mat-autocomplete die Verlaufsliste des Browsers fern,
      // die ein unbekannter Wert wieder zuliesse. Ein einzelnes Feld mit "off" (Strasse) reicht
      // Chrome und Edge nicht fuer eine Adresse, solange PLZ und Ort den unbekannten Wert tragen.
      if (!feld.hasAttribute('data-browser-autofill') && feld.getAttribute('autocomplete') !== 'off') {
        feld.setAttribute('autocomplete', KEIN_AUTOFILL)
      }
    }
    document.querySelectorAll(FELDER).forEach(markieren)
    new MutationObserver((aenderungen) => {
      for (const aenderung of aenderungen) {
        for (const knoten of aenderung.addedNodes) {
          if (knoten instanceof Element) {
            if (knoten.matches(FELDER)) {
              markieren(knoten)
            }
            knoten.querySelectorAll(FELDER).forEach(markieren)
          }
        }
      }
    }).observe(document.body, { childList: true, subtree: true })
  })
}

/**
 * Das Mausrad aendert keine Zahl in einem Eingabefeld, in allen drei Apps. Begruendung an
 * `zahlenfeldOhneMausrad`. Ein Listener am Dokument erreicht auch die Felder in Dialogen.
 */
export function provideZahlenfelderOhneMausrad(): EnvironmentProviders {
  return provideAppInitializer(() => {
    inject(DOCUMENT).addEventListener('wheel', zahlenfeldOhneMausrad, { capture: true, passive: true })
  })
}
